import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORIES, GAME_PAGES, SORTS } from "@/lib/constants";
import { ProductCard } from "@/components/shop/ProductCard";
import { EventRow } from "@/pages/EventsPage";
import { Pager, totalOf } from "@/components/shop/Pager";

const NotFound = () => <p className="py-32 text-center text-stone-600" data-testid="game-not-found">Gioco non trovato. <Link to="/" className="text-amber-600 underline">Torna alla home</Link></p>;

const Tab = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full border-2 px-4 py-1.5 text-sm font-semibold transition-colors ${active ? "border-black bg-amber-500 text-black" : "border-black/10 bg-white text-stone-600 hover:border-black/40"}`}>
    {children}
  </button>
);

export default function GamePage() {
  const { slug } = useParams();
  const game = GAME_PAGES.find((g) => g.slug === slug);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const sort = params.get("sort") || "recenti";
  const page = parseInt(params.get("page") || "1", 10);
  const [products, setProducts] = useState(null);
  const [total, setTotal] = useState(0);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!game) return;
    setProducts(null);
    const games = category === "accessori" ? `${game.name},Universale` : game.name;
    api.get("/products", { params: { game: games, category: category || undefined, sort, page, limit: 24 } })
      .then((r) => { setProducts(r.data); setTotal(totalOf(r)); });
  }, [game, category, sort, page]);
  useEffect(() => {
    if (game) api.get("/events", { params: { game: game.name, limit: 3 } }).then((r) => setEvents(r.data));
  }, [game]);

  if (!game) return <NotFound />;
  const set = (k, v) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== "page") n.delete("page"); setParams(n); };

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-10 sm:px-6 lg:px-12">
      <div className="box-pattern sticker grid items-center gap-8 rounded-3xl p-6 md:grid-cols-[320px_1fr] md:p-10">
        <div className="flex h-36 items-center justify-center rounded-2xl border-2 border-black bg-black p-6 shadow-[4px_4px_0_#000]">
          <img src={game.logo} alt={game.title} className="max-h-full max-w-full object-contain" data-testid="game-page-logo" />
        </div>
        <div className="text-black">
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-5xl" data-testid="game-page-title">{game.title}</h1>
          <p className="mt-3 max-w-xl font-semibold text-black/70">{game.text}</p>
        </div>
      </div>

      <div className="mt-6 hidden flex-wrap gap-2 sm:flex" data-testid="game-switcher">
        {GAME_PAGES.map((g) => (
          <Link key={g.slug} to={`/gioco/${g.slug}`} data-testid={`game-switch-${g.slug}`}
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold ${g.slug === slug ? "bg-black text-white" : "bg-black/5 text-stone-700 hover:bg-black/10"}`}>{g.name}</Link>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <Tab active={!category} onClick={() => set("category", "")} testid="game-tab-all">Tutto</Tab>
          {CATEGORIES.filter((c) => c.value !== "gadget").map((c) => (
            <Tab key={c.value} active={category === c.value} onClick={() => set("category", c.value)} testid={`game-tab-${c.value}`}>{c.label}</Tab>
          ))}
        </div>
        <select value={sort} onChange={(e) => set("sort", e.target.value)} data-testid="game-sort" className="h-10 rounded-full border-2 border-black/10 bg-white px-4 text-sm">
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4" data-testid="game-products">
        {products === null && <p className="col-span-full py-10 text-stone-500">Caricamento...</p>}
        {products?.length === 0 && <p className="col-span-full rounded-2xl border border-dashed border-black/15 p-10 text-center text-stone-500" data-testid="game-products-empty">Nessun prodotto disponibile in questa sezione. Torna presto!</p>}
        {products?.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
      </div>
      {products && <Pager page={page} total={total} size={24} onPage={(p) => set("page", p > 1 ? String(p) : "")} testid="game-pager" />}

      {events.length > 0 && (
        <section className="mt-16" data-testid="game-events">
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-display text-2xl font-bold sm:text-3xl">Prossimi eventi {game.name}</h2>
            <Link to="/eventi" className="text-sm font-semibold text-amber-600 hover:underline">Tutti gli eventi →</Link>
          </div>
          <div className="space-y-3">{events.map((e) => <EventRow key={e.id} e={e} />)}</div>
        </section>
      )}
    </div>
  );
}
